import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import styled from '@emotion/styled';
import { css, SerializedStyles } from '@emotion/react';
import { withConfig } from 'src/withConfig';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';

interface PropsType {
    variant: DefaultComponentVariantType | null;
    borderBottom?: boolean;
    borderLeft?: boolean;
    borderRight?: boolean;
    borderTop?: boolean;
    isDisabled?: boolean;
    isEmpty?: boolean;
    isEventPage?: boolean;
    isOddsDown?: boolean;
    isOddsUp?: boolean;
    isSelected?: boolean;
    isShowParticipantName?: boolean;
    isSuspended?: boolean;
    isExpandedLayout?: boolean;
}

export const SelectionParticipantName = withConfig(theme => styled('span', { label: 'SelectionParticipantName' })`
    color: ${theme.star.eventRow.txtColor};
    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.regular};
    padding-right: 5px;
    transition: color 0.2s ease;
`);

const setDisabled = (theme: EmotionTheme, isDisabled: boolean): SerializedStyles | null => {
    if (isDisabled === true) {
        return css`
            background-color: ${theme.star.selection.bgColorSenary};
            color: ${theme.star.selection.txtColorSecondary};
            pointer-events: none;
            user-select: none;
        `;
    }
    return null;
};

const setSuspended = (theme: EmotionTheme, isSuspended: boolean, variant: DefaultComponentVariantType | null): SerializedStyles | null => {
    if (isSuspended === true) {
        if (variant === DefaultComponentVariantType.TERTIARY) {
          return css`
              pointer-events: none;
              user-select: none;

              & .selection-suspended--icon g > rect {
                  fill: ${theme.star.unified.selection.txtColorQuinary};
              }

              &::before {
                  display: none;
              }
          `
        }
        return css`
            pointer-events: none;
            user-select: none;
            &::before {
                align-items: center;
                content: 'Susp';
                display: flex;
                font-size: ${theme.star.fontSize.small};
                font-weight: ${theme.star.fontWeight.regular};
                justify-content: center;
                line-height: 1.3334;
                margin: auto;
                opacity: 1;
                text-transform: uppercase;
                z-index: ${theme.star.zIndexGlobal.base};
                @media ${theme.star.mediaQuery.tablet} {
                    font-size: ${theme.star.fontSize.xRegular};
                }
            }
        `;
    }
    return null;
};

const setEmpty = (isEmpty: boolean): SerializedStyles | null => {
    if (isEmpty === true) {
        return css`
            pointer-events: none;
            user-select: none;
        `;
    }
    return null;
};

const setBorders = (
    theme: EmotionTheme,
    borderTop: boolean,
    borderRight: boolean,
    borderBottom: boolean,
    borderLeft: boolean
): string => {
    const out: Array<string> = [];
    if (borderTop) {
        out.push(`border-top: 1px solid ${theme.star.selection.borderColor};`);
    }
    if (borderRight) {
        out.push(`border-right: 1px solid ${theme.star.selection.borderColor};`);
    }
    if (borderBottom) {
        out.push(`border-bottom: 1px solid ${theme.star.selection.borderColor};`);
    }
    if (borderLeft) {
        out.push(`border-left: 1px solid ${theme.star.selection.borderColor};`);
    }
    return out.join('\n');
};

const setTxtAndBackgroundColor = (
    variant: DefaultComponentVariantType | null,
    theme: EmotionTheme,
    isSelected: boolean,
    isSuspended: boolean,
    isOddsDown: boolean,
    isOddsUp: boolean
): SerializedStyles => {
    if (isSelected === true) {
        return css`
            color: ${variant === DefaultComponentVariantType.TERTIARY ? theme.star.unified.selection.txtColorTertiary : theme.star.selection.txtColorTertiary};
            [class$='EventRowSelectionLine'],
            [class$='SelectionParticipantName'] {
                color: currentcolor;
            }
            &::before {
                background-color: ${variant === DefaultComponentVariantType.TERTIARY ? theme.star.oddsUnified.oddsSelected : theme.star.selection.bgColorQuinary};
                opacity: 1;
            }
            > span {
                color: ${variant === DefaultComponentVariantType.TERTIARY ? theme.star.unified.selection.txtColorTertiary : theme.star.selection.txtColorTertiary};
            }
            @media screen and (hover: hover) and (pointer: fine) {
                &::after {
                    background-color: ${theme.star.selection.bgColorSecondary};
                }
                &:hover {
                    border-radius: ${variant === DefaultComponentVariantType.SECONDARY ? theme.star.radius.small : variant === DefaultComponentVariantType.TERTIARY ? theme.star.radius.small : '0'};
                    &::after {
                        opacity: 1;
                    }
                }
            }
        `;
    }
    if (isSuspended === true) {
        return css`
            color: ${theme.star.odds.txtColorSecondary};
            &::before {
                background-color: ${variant === DefaultComponentVariantType.TERTIARY ?  theme.star.unified.selection.bgColorDefault: theme.star.odds.bgColorTertiary};
                opacity: 1;
            }
        `;
    }
    if (isOddsDown === true) {
        return css`
            color: ${theme.star.odds.txtColor};
            [class$='EventRowSelectionLine'],
            [class$='SelectionParticipantName'] {
                color: currentcolor;
            }
            &::before {
                background-color: ${variant === DefaultComponentVariantType.TERTIARY ? theme.star.oddsUnified.bgColorSecondary : theme.star.odds.bgColorSecondary};
                opacity: 1;
            }
        `;
    }
    if (isOddsUp === true) {
        return css`
            color: ${theme.star.odds.txtColor};
            [class$='EventRowSelectionLine'],
            [class$='SelectionParticipantName'] {
                color: currentcolor;
            }
            &::before {
                background-color: ${variant === DefaultComponentVariantType.TERTIARY ? theme.star.oddsUnified.bgColor : theme.star.odds.bgColor};
                opacity: 1;
            }
        `;
    }

    return css`
        color: ${theme.star.selection.txtColor};

        [class$='SelectionParticipantName'] {
            color: ${variant === DefaultComponentVariantType.TERTIARY ? theme.star.unified.eventRow.txtColorSecondary : theme.star.eventRow.txtColor};
        }
        
        @media screen and (hover: hover) and (pointer: fine) {
            &:hover {
                color: ${variant === DefaultComponentVariantType.TERTIARY ? theme.star.unified.selection.txtColorTertiary : theme.star.selection.txtColorTertiary};

                [class$='EventRowSelectionLine'], [class$='SelectionParticipantName'] {
                    color: currentcolor;
                }
                &::before {
                    background-color: ${variant === DefaultComponentVariantType.TERTIARY ? theme.star.unified.selection.bgColorQuinary : theme.star.selection.bgColorQuinary};
                    opacity: 1;
                }
                > span {
                    color: ${theme.star.selection.txtColorTertiary};
                }
            }
        }
    `;
};

const setSelectionItemStyle = (
    variant: DefaultComponentVariantType | null,
    theme: EmotionTheme,
    isExpandedLayout: boolean
): SerializedStyles => {
    const unifiedCSS = `
        background: ${theme.star.selection.bgColorSeptenary};
        border-radius: ${theme.star.radius.small};
        border: 0;
        width: 48px;
        flex-basis: 48px;
        @media ${theme.star.mediaQuery.tablet} {
            width: 64px;
            flex-basis: 64px;
            font-size: ${theme.star.fontSize.xRegular};
        }
        &::before {
            border-radius: ${theme.star.radius.small};
        }
    `;
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            ${unifiedCSS}
        `;
    }
    if (variant === DefaultComponentVariantType.TERTIARY) {
        if (isExpandedLayout === true) {
            return css`
                background: ${theme.star.unified.selection.bgColorDefault};
                border-radius: ${theme.star.radius.small};
                border: 0;
                flex: none;
                width: 100%;
                min-width: 65px;
                min-height: 44px;
                font-size: ${theme.star.fontSize.xRegular};
                &::before {
                    border-radius: ${theme.star.radius.small};
                }
            `;
        }

        return css`
            background: ${theme.star.unified.selection.bgColorDefault};
            border-radius: ${theme.star.radius.small};
            border: 0;
            flex: none;
            min-width: 56px;
            min-height: 44px;
            font-size: ${theme.star.fontSize.xRegular};
            &::before {
                border-radius: ${theme.star.radius.small};
            }
        `;
    }
    return css`
        width: 50px;

        .unified & {
            ${ unifiedCSS }
        }
        @media ${theme.star.mediaQuery.tablet} {
            flex-basis: 68px;
            font-size: ${theme.star.fontSize.xRegular};
            width: 68px;
        }
    `;
};

export const SelectionItemStyle = withConfig(theme => styled('a', { label: 'SelectionItemStyle' })<PropsType>`
    align-items: center;
    display: flex;
    flex: 0 0 50px;
    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.bold};
    justify-content: center;
    line-height: 1.43;
    margin-left: -1px;
    position: relative;
    text-align: center;
    text-decoration: none;
    transition: color 0.2s ease;
    z-index: ${theme.star.zIndexGlobal.base};
    &::after,
    &::before {
        bottom: 0;
        content: '';
        left: 0;
        opacity: 0;
        position: absolute;
        right: 0;
        top: 0;
        transition: opacity 0.2s ease;
        z-index: ${theme.star.zIndexGlobal.below};
    }
    ${({ isShowParticipantName }): SerializedStyles =>
        isShowParticipantName === true
            ? css`
                justify-content: space-between;
                width: 100%;
                padding: 0 8px;
            ` : css``};

    ${({ isSelected = false, isSuspended = false, isOddsDown = false, isOddsUp = false, variant }): SerializedStyles =>
        setTxtAndBackgroundColor(variant, theme, isSelected, isSuspended, isOddsDown, isOddsUp)};

    ${({ borderTop = false, borderRight = false, borderBottom = false, borderLeft = false }): string =>
        setBorders(theme, borderTop, borderRight, borderBottom, borderLeft)};

    ${({ isDisabled }): SerializedStyles | null => (isDisabled === true ? setDisabled(theme, isDisabled) : null)};

    ${({ isSuspended, variant }): SerializedStyles | null =>
        isSuspended === true ? setSuspended(theme, isSuspended, variant) : null};

    ${({ isEmpty }): SerializedStyles | null => (isEmpty === true ? setEmpty(isEmpty) : null)};
    ${({ variant, isExpandedLayout = false }): SerializedStyles =>
        setSelectionItemStyle(variant, theme, isExpandedLayout === true)}
`);
